import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import styles from "./BenchmarkHeatmap.module.scss";
import { formatBenchmarkStatValue } from "../../benchmarkMetricConfig";
import { buildKeywordInstitutionMatrix } from "../../benchmarkChartDerivations";

const BenchmarkHeatmap = ({
  rawData,
  institutionIds,
  metricKey,
  metricLabel,
  metric,
  metricOptions = [],
  onMetricChange,
  entityLabel = "Institution",
  exportMode = false,
}) => {
  const chartRef = useRef(null);

  const matrix = useMemo(
    () => buildKeywordInstitutionMatrix(rawData, institutionIds, metricKey),
    [rawData, institutionIds, metricKey]
  );

  useEffect(() => {
    if (!chartRef.current || !matrix.keywords.length || !matrix.institutions.length) {
      return undefined;
    }

    const chart = echarts.init(chartRef.current);
    const option = {
      animation: !exportMode,
      grid: {
        left: 120,
        right: 28,
        top: 24,
        /* Room for rotated institution labels + horizontal color bar below */
        bottom: 108,
      },
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const inst = matrix.institutions[params.data[0]];
          const researchTopic = matrix.keywords[params.data[1]];
          const val = params.data[2];
          const shown = formatBenchmarkStatValue(val, metric || { decimals: 0 });
          return `${inst?.name || entityLabel}<br/>${researchTopic}<br/><b>${shown}</b>`;
        },
      },
      xAxis: {
        type: "category",
        data: matrix.institutions.map((i) => i.name),
        axisLabel: {
          interval: 0,
          rotate: 22,
          fontSize: 11,
          margin: 10,
        },
        splitArea: { show: false },
      },
      yAxis: {
        type: "category",
        data: matrix.keywords,
        axisLabel: {
          fontSize: 11,
        },
        splitArea: { show: false },
      },
      visualMap: {
        min: matrix.minValue,
        max: matrix.maxValue <= matrix.minValue ? matrix.minValue + 1 : matrix.maxValue,
        calculable: true,
        orient: "horizontal",
        left: "center",
        bottom: 8,
        itemWidth: 12,
        textStyle: { fontSize: 11 },
      },
      series: [
        {
          type: "heatmap",
          data: matrix.values,
          progressive: 0,
          label: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 12,
              shadowColor: "rgba(0, 0, 0, 0.2)",
            },
          },
        },
      ],
    };

    chart.setOption(option);
    const onResize = () => chart.resize();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      chart.dispose();
    };
  }, [matrix, metric, entityLabel, exportMode]);

  return (
    <section
      className={styles.card}
      aria-labelledby="benchmark-heatmap-title"
      style={
        exportMode
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 8,
        }}
      >
        <h3
          id="benchmark-heatmap-title"
          className={styles.title}
          style={{ margin: 0 }}
          data-report-strip="true"
        >
          {entityLabel} Heatmap
        </h3>
        {metricOptions.length > 0 && onMetricChange ? (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="heatmap-metric-select-label">Metric</InputLabel>
            <Select
              labelId="heatmap-metric-select-label"
              value={metricKey}
              label="Metric"
              onChange={onMetricChange}
              aria-label="Select metric for the heatmap"
            >
              {metricOptions.map((stat) => (
                <MenuItem key={stat.key} value={stat.key}>
                  {stat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
      </div>
      {!matrix.keywords.length || !matrix.institutions.length ? (
        <div className={styles.empty}>Not enough data to render the heatmap.</div>
      ) : (
        <div
          ref={chartRef}
          className={styles.chart}
          style={exportMode ? { flex: 1, minHeight: 0, height: "auto" } : undefined}
          role="img"
          aria-label={`Heatmap of ${metricLabel} across research topics and ${entityLabel.toLowerCase()}s`}
        />
      )}
    </section>
  );
};

export default BenchmarkHeatmap;
